import React, { useState } from 'react';
import axios from 'axios';
import { theme, inputStyle, labelStyle } from '../theme';
import { OralzyLogo } from './LandingPage';
import { getDeviceFingerprint, getDeviceContext } from '../utils/deviceFingerprint';

/* Oralzy brand palette (from logo) */
const OZ_ORANGE = '#F57C1F';
const OZ_ORANGE_DARK = '#DC6A0F';
const OZ_BLUE = '#29A8E0';

/**
 * Self-contained Login + Signup screen.
 *
 * Props:
 *   API_BASE  — base URL for /auth endpoints
 *   onLogin   — callback(user) called after successful login.
 *               Parent should persist the user and switch to the app shell.
 */
const LoginPage = ({ API_BASE, onLogin, onClinicSignup }) => {
  const [authMode, setAuthMode] = useState('login');   // 'login' | 'signup' | 'forgot'
  const [authForm, setAuthForm] = useState({ username: '', password: '', fullName: '' });
  const [authMsg, setAuthMsg]   = useState({ type: '', text: '' });
  const [authLoading, setAuthLoading] = useState(false);

  // Forgot-password 2-step state
  const [forgotPhase,    setForgotPhase]    = useState('request');  // 'request' | 'verify'
  const [forgotMaskedNo, setForgotMaskedNo] = useState('');
  const [forgotChannel,  setForgotChannel]  = useState('whatsapp');
  const [forgotOtp,      setForgotOtp]      = useState('');
  const [forgotNewPwd,   setForgotNewPwd]   = useState('');

  // 2Factor.in Phone OTP login state
  const [showPhoneOtp,  setShowPhoneOtp]  = useState(false);
  const [phoneStep,     setPhoneStep]     = useState('enter-phone'); // 'enter-phone' | 'enter-otp'
  const [phoneInput,    setPhoneInput]    = useState('');
  const [phoneOtpInput, setPhoneOtpInput] = useState('');
  const [phoneMsg,      setPhoneMsg]      = useState({ type: '', text: '' });

  // New-device voice-OTP challenge state. When set, password fields hide and
  // a code-input screen shows instead. Cleared on success/cancel.
  const [deviceChallenge, setDeviceChallenge] = useState(null); // { challengeId, maskedPhone } | null
  const [deviceOtp,       setDeviceOtp]       = useState('');
  const [trustDevice,     setTrustDevice]     = useState(true);

  const handleSendPhoneOTP = async () => {
    if (!phoneInput || phoneInput.replace(/\D/g, '').length !== 10) {
      return setPhoneMsg({ type: 'error', text: 'Enter a valid 10-digit mobile number' });
    }
    setAuthLoading(true);
    try {
      await axios.post(`${API_BASE}/saas-auth/phone/send-otp`, { mobile: phoneInput, purpose: 'login' });
      setPhoneStep('enter-otp');
      setPhoneMsg({ type: 'success', text: `📞 Call aa rahi hai +91-${phoneInput} pe — OTP suno aur dalo` });
    } catch (e) {
      setPhoneMsg({ type: 'error', text: e.response?.data?.error || 'Failed to send OTP' });
    }
    setAuthLoading(false);
  };

  const handleVerifyPhoneOTP = async () => {
    if (phoneOtpInput.length < 4) return setPhoneMsg({ type: 'error', text: 'Enter OTP (4-6 digits)' });
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/saas-auth/phone/login`, {
        mobile: phoneInput, otp: phoneOtpInput,
      });
      if (res.data.accessToken) localStorage.setItem('dentalToken', res.data.accessToken);
      if (res.data.refreshToken) localStorage.setItem('dentalRefreshToken', res.data.refreshToken);
      if (res.data.user) {
        localStorage.setItem('dentalUser', JSON.stringify(res.data.user));
        onLogin(res.data.user);
      }
    } catch (e) {
      setPhoneMsg({ type: 'error', text: e.response?.data?.error || 'Verification failed' });
    }
    setAuthLoading(false);
  };

  const closePhoneOtp = () => {
    setShowPhoneOtp(false);
    setPhoneStep('enter-phone');
    setPhoneInput(''); setPhoneOtpInput('');
    setPhoneMsg({ type: '', text: '' });
  };

  const logFailedLogin = async () => {
    try {
      const fp = await getDeviceFingerprint();
      const ctx = getDeviceContext();
      await axios.post(`${API_BASE}/security/log-failed-login`, {
        username: authForm.username, fingerprint: fp,
        userAgent: ctx.userAgent, platform: ctx.platform,
      });
    } catch {}
  };

  const handleLogin = async () => {
    if (!authForm.username || !authForm.password) {
      return setAuthMsg({ type:'error', text:'Username and password are required' });
    }
    setAuthLoading(true);
    try {
      // Hardware fingerprint — lets the server skip OTP on a previously
      // trusted device. Computed live (not cached) so a physical swap
      // invalidates trust automatically.
      const fingerprint = await getDeviceFingerprint();
      const res = await axios.post(`${API_BASE}/saas-auth/login`, {
        username: authForm.username,
        password: authForm.password,
        deviceFingerprint: fingerprint,
      });
      localStorage.setItem('dentalUser', JSON.stringify(res.data.user));
      if (res.data.accessToken)  localStorage.setItem('dentalToken',        res.data.accessToken);
      if (res.data.refreshToken) localStorage.setItem('dentalRefreshToken', res.data.refreshToken);
      onLogin(res.data.user);
    } catch (e) {
      // New-device path: server accepted the password but wants a voice OTP
      // read out to the clinic owner's mobile before handing tokens over.
      if (e.response?.data?.code === 'DEVICE_OTP_REQUIRED') {
        setDeviceChallenge({
          challengeId: e.response.data.challengeId,
          maskedPhone: e.response.data.maskedPhone,
        });
        setAuthMsg({ type:'success', text: e.response.data.message || `Owner ke mobile (${e.response.data.maskedPhone}) pe call aayegi — 6-digit code suno aur dalo.` });
        setAuthLoading(false);
        return;
      }
      logFailedLogin();        // fire-and-forget; alert admin
      setAuthMsg({ type:'error', text: e.response?.data?.error || e.response?.data?.message || 'Login failed' });
    }
    setAuthLoading(false);
  };

  const handleVerifyDeviceOtp = async () => {
    if (!deviceChallenge) return;
    if (deviceOtp.length < 4) return setAuthMsg({ type:'error', text:'6-digit OTP daalo' });
    setAuthLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/saas-auth/login/verify-device`, {
        challengeId: deviceChallenge.challengeId,
        otp: deviceOtp,
        trustDevice,
      });
      localStorage.setItem('dentalUser', JSON.stringify(res.data.user));
      if (res.data.accessToken)  localStorage.setItem('dentalToken',        res.data.accessToken);
      if (res.data.refreshToken) localStorage.setItem('dentalRefreshToken', res.data.refreshToken);
      setDeviceChallenge(null); setDeviceOtp('');
      onLogin(res.data.user);
    } catch (e) {
      setAuthMsg({ type:'error', text: e.response?.data?.error || 'OTP verify failed' });
    }
    setAuthLoading(false);
  };

  const cancelDeviceChallenge = () => {
    setDeviceChallenge(null); setDeviceOtp(''); setTrustDevice(true);
    setAuthMsg({ type:'', text:'' });
  };

  const handleForgotRequest = async () => {
    if (!authForm.username) return setAuthMsg({ type:'error', text:'Enter your username first' });
    setAuthLoading(true);
    try {
      const r = await axios.post(`${API_BASE}/security/password/request-reset`, { username: authForm.username });
      setForgotMaskedNo(r.data.masked || '');
      setForgotChannel(r.data.channel || 'whatsapp');
      setForgotPhase('verify');
      setAuthMsg({ type:'success', text:`OTP sent via ${r.data.channel} to ${r.data.masked}` });
    } catch (e) {
      setAuthMsg({ type:'error', text: e.response?.data?.error || 'Failed to send OTP' });
    }
    setAuthLoading(false);
  };

  const handleForgotVerify = async () => {
    if (forgotOtp.length !== 6) return setAuthMsg({ type:'error', text:'Enter 6-digit OTP' });
    if (forgotNewPwd.length < 6) return setAuthMsg({ type:'error', text:'New password must be 6+ characters' });
    setAuthLoading(true);
    try {
      const fp = await getDeviceFingerprint();
      await axios.post(`${API_BASE}/security/password/verify-reset`, {
        username: authForm.username, otp: forgotOtp, newPassword: forgotNewPwd, fingerprint: fp,
      });
      setAuthMsg({ type:'success', text:'Password updated! Sign in with your new password.' });
      setAuthMode('login');
      setForgotPhase('request');
      setForgotOtp(''); setForgotNewPwd('');
      setAuthForm({ ...authForm, password: '' });
    } catch (e) {
      setAuthMsg({ type:'error', text: e.response?.data?.error || 'Reset failed' });
    }
    setAuthLoading(false);
  };

  const handleSignup = async () => {
    if (!authForm.fullName || !authForm.username || !authForm.password) {
      return setAuthMsg({ type:'error', text:'All fields are required' });
    }
    setAuthLoading(true);
    try {
      await axios.post(`${API_BASE}/saas-auth/signup`, authForm);
      setAuthMsg({ type:'success', text:'Signup successful! Please wait for admin approval.' });
      setAuthForm({ username:'', password:'', fullName:'' });
    } catch (e) {
      setAuthMsg({ type:'error', text: e.response?.data?.message || 'Signup failed' });
    }
    setAuthLoading(false);
  };

  return (
    <div style={{minHeight:'100vh', backgroundColor:theme.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:'20px'}}>
      <div style={{
        backgroundColor:theme.surface,
        borderRadius:'20px',
        padding:'48px 44px',
        width:'420px', maxWidth:'95vw',
        boxShadow: theme.shadowLg,
        border: `1px solid ${theme.border}`,
      }}>
        {/* Logo */}
        <div style={{textAlign:'center', marginBottom:'32px'}}>
          <div style={{display:'flex', justifyContent:'center', marginBottom:'10px'}}>
            <OralzyLogo height={52} />
          </div>
          <p style={{color:theme.textMuted, fontSize:'0.86rem', margin:0}}>The operating system for modern dentistry</p>
        </div>

        {/* New clinic signup CTA */}
        {onClinicSignup && (
          <div style={{
            background: `linear-gradient(135deg, ${OZ_ORANGE}15, ${OZ_BLUE}15)`,
            padding: '12px 14px', borderRadius: '10px', marginBottom: '18px',
            border: `1px dashed ${OZ_ORANGE}40`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px',
          }}>
            <div style={{ fontSize: '0.82rem', color: theme.navy }}>
              Naya clinic shuru karna hai?
            </div>
            <button onClick={onClinicSignup} style={{
              padding: '8px 14px', background: OZ_ORANGE, color: '#fff', border: 'none',
              borderRadius: '6px', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              Start Free Trial →
            </button>
          </div>
        )}

        {/* Auth tabs */}
        <div style={{display:'flex', marginBottom:'24px', backgroundColor:theme.bg, borderRadius:theme.radius, padding:'4px', gap:'4px'}}>
          <button onClick={()=>{ setAuthMode('login'); setAuthMsg({type:'',text:''}); setForgotPhase('request'); }}
            style={{flex:1, padding:'10px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem', borderRadius:theme.radiusSm,
              backgroundColor: authMode==='login' ? theme.surface : 'transparent',
              color: authMode==='login' ? theme.navy : theme.textMuted,
              boxShadow: authMode==='login' ? theme.shadowSm : 'none'}}>
            Sign In
          </button>
          <button onClick={()=>{ setAuthMode('signup'); setAuthMsg({type:'',text:''}); }}
            style={{flex:1, padding:'10px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem', borderRadius:theme.radiusSm,
              backgroundColor: authMode==='signup' ? theme.surface : 'transparent',
              color: authMode==='signup' ? theme.navy : theme.textMuted,
              boxShadow: authMode==='signup' ? theme.shadowSm : 'none'}}>
            Request Access
          </button>
          <button onClick={()=>{ setAuthMode('forgot'); setAuthMsg({type:'',text:''}); setForgotPhase('request'); }}
            style={{flex:1, padding:'10px', border:'none', cursor:'pointer', fontWeight:'600', fontSize:'0.86rem', borderRadius:theme.radiusSm,
              backgroundColor: authMode==='forgot' ? theme.surface : 'transparent',
              color: authMode==='forgot' ? theme.navy : theme.textMuted,
              boxShadow: authMode==='forgot' ? theme.shadowSm : 'none'}}>
            Forgot
          </button>
        </div>

        {/* Message */}
        {authMsg.text && (
          <div style={{padding:'11px 14px', borderRadius:theme.radiusSm, marginBottom:'16px', fontSize:'0.85rem', fontWeight:'500',
            backgroundColor: authMsg.type==='error' ? '#fef2f2' : '#f0fdf4',
            color: authMsg.type==='error' ? theme.danger : theme.success,
            border: `1px solid ${authMsg.type==='error' ? '#fecaca' : '#bbf7d0'}`}}>
            {authMsg.text}
          </div>
        )}

        {authMode === 'signup' && (
          <div style={{marginBottom:'14px'}}>
            <label style={labelStyle}>Full Name</label>
            <input placeholder="Enter your full name" value={authForm.fullName}
              onChange={e=>setAuthForm({...authForm, fullName:e.target.value})} style={inputStyle}/>
          </div>
        )}

        {/* Username + password — hidden while a voice-OTP challenge is active */}
        {!deviceChallenge && (
          <div style={{marginBottom:'14px'}}>
            <label style={labelStyle}>Username</label>
            <input placeholder="Username" value={authForm.username}
              onChange={e=>setAuthForm({...authForm, username:e.target.value})} style={inputStyle}
              onKeyDown={e=>{ if(e.key==='Enter' && authMode==='login') handleLogin(); }}/>
          </div>
        )}

        {/* Password field — hidden during forgot/verify and device-OTP challenge */}
        {authMode !== 'forgot' && !deviceChallenge && (
          <div style={{marginBottom:'24px'}}>
            <label style={labelStyle}>Password</label>
            <input type="password" placeholder="Password" value={authForm.password}
              onChange={e=>setAuthForm({...authForm, password:e.target.value})} style={inputStyle}
              onKeyDown={e=>{ if(e.key==='Enter' && authMode==='login') handleLogin(); }}/>
          </div>
        )}

        {/* Device voice-OTP challenge — password was correct but this is a new device */}
        {deviceChallenge && (<>
          <div style={{marginBottom:'14px'}}>
            <label style={labelStyle}>
              OTP (6 digits — call on {deviceChallenge.maskedPhone})
            </label>
            <input value={deviceOtp}
              onChange={e=>setDeviceOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
              onKeyDown={e=>{ if(e.key==='Enter') handleVerifyDeviceOtp(); }}
              style={{...inputStyle, fontSize:'1.1rem', letterSpacing:'0.4em', textAlign:'center', fontFamily:'monospace'}}
              placeholder="000000" maxLength={6} autoFocus/>
          </div>
          <label style={{display:'flex', alignItems:'center', gap:8, marginBottom:18, fontSize:'0.84rem', color:theme.textSubtle, cursor:'pointer'}}>
            <input type="checkbox" checked={trustDevice} onChange={e=>setTrustDevice(e.target.checked)}/>
            <span>Is device ko 90 din ke liye trust karo (next login sirf password se)</span>
          </label>
          <button onClick={cancelDeviceChallenge} type="button"
            style={{background:'none', border:'none', color:theme.textMuted, fontSize:'0.82rem', cursor:'pointer', marginBottom:8, padding:0}}>
            ← Back (dobara login karo)
          </button>
        </>)}

        {/* Forgot password — Step 2 (OTP + new password) */}
        {authMode === 'forgot' && forgotPhase === 'verify' && (<>
          <div style={{marginBottom:'14px'}}>
            <label style={labelStyle}>OTP (6 digits sent to {forgotMaskedNo} via {forgotChannel})</label>
            <input value={forgotOtp} onChange={e=>setForgotOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
              style={{...inputStyle, fontSize:'1.1rem', letterSpacing:'0.4em', textAlign:'center', fontFamily:'monospace'}}
              placeholder="000000" maxLength={6}/>
          </div>
          <div style={{marginBottom:'18px'}}>
            <label style={labelStyle}>New Password</label>
            <input type="password" value={forgotNewPwd} onChange={e=>setForgotNewPwd(e.target.value)} style={inputStyle} placeholder="6+ characters"/>
          </div>
        </>)}

        <button
          onClick={
            deviceChallenge ? handleVerifyDeviceOtp :
            authMode==='login'  ? handleLogin :
            authMode==='signup' ? handleSignup :
            forgotPhase==='request' ? handleForgotRequest : handleForgotVerify
          }
          disabled={authLoading}
          style={{
            width:'100%', padding:'13px',
            background:`linear-gradient(135deg, ${OZ_ORANGE} 0%, ${OZ_ORANGE_DARK} 60%, ${OZ_BLUE} 100%)`,
            color:'white', border:'none', borderRadius:theme.radius,
            cursor: authLoading ? 'not-allowed' : 'pointer',
            fontWeight:'600', fontSize:'0.95rem',
            boxShadow:'0 6px 18px rgba(245,124,31,0.35)',
            opacity: authLoading ? 0.7 : 1,
          }}>
          {authLoading ? 'Please wait...'
            : deviceChallenge ? 'Verify OTP & Sign In'
            : authMode==='login' ? 'Sign In'
            : authMode==='signup' ? 'Request Access'
            : forgotPhase==='request' ? 'Send OTP to my mobile'
            : 'Reset Password'}
        </button>

        {authMode==='signup' && (
          <p style={{textAlign:'center', fontSize:'0.78rem', color:theme.textMuted, margin:'18px 0 0'}}>
            An administrator will review your request before access is granted.
          </p>
        )}
        {authMode==='login' && (
          <>
            <div style={{display:'flex', alignItems:'center', gap:10, margin:'20px 0 14px'}}>
              <div style={{flex:1, height:1, background:theme.border}}/>
              <span style={{fontSize:'0.75rem', color:theme.textSubtle, fontWeight:600}}>OR</span>
              <div style={{flex:1, height:1, background:theme.border}}/>
            </div>
            <button onClick={() => setShowPhoneOtp(true)}
              style={{
                width:'100%', padding:'11px', background:'#fff',
                color: OZ_BLUE, border:`2px solid ${OZ_BLUE}`, borderRadius:theme.radius,
                cursor:'pointer', fontWeight:600, fontSize:'0.9rem',
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
              }}>
              📞 Sign in with Voice Call OTP
            </button>
            <p style={{textAlign:'center', fontSize:'0.78rem', color:theme.textSubtle, margin:'18px 0 0'}}>
              Need access? Contact your clinic administrator.
            </p>
          </>
        )}
      </div>

      {/* Firebase Phone OTP modal */}
      {showPhoneOtp && (
        <div style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000, padding:20,
        }}>
          <div style={{
            background:'#fff', borderRadius:16, padding:'32px 28px',
            width:400, maxWidth:'95vw', boxShadow:theme.shadowLg,
          }}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18}}>
              <h3 style={{margin:0, color:theme.navy, fontSize:'1.1rem'}}>📞 Sign in with Voice OTP</h3>
              <button onClick={closePhoneOtp} style={{background:'none', border:'none', fontSize:20, cursor:'pointer', color:theme.textMuted}}>×</button>
            </div>

            {phoneMsg.text && (
              <div style={{
                padding:'10px 12px', borderRadius:8, marginBottom:14, fontSize:'0.82rem', fontWeight:500,
                background: phoneMsg.type === 'error' ? '#fef2f2' : '#f0fdf4',
                color: phoneMsg.type === 'error' ? theme.danger : theme.success,
                border:`1px solid ${phoneMsg.type === 'error' ? '#fecaca' : '#bbf7d0'}`,
              }}>{phoneMsg.text}</div>
            )}

            {phoneStep === 'enter-phone' && (
              <>
                <div style={{marginBottom:14}}>
                  <label style={labelStyle}>Mobile Number</label>
                  <input placeholder="9685058003" value={phoneInput}
                    onChange={e => setPhoneInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                    style={{...inputStyle, fontSize:'1rem'}}
                    onKeyDown={e => { if (e.key === 'Enter') handleSendPhoneOTP(); }}/>
                  <p style={{fontSize:'0.75rem', color:theme.textMuted, margin:'6px 0 0'}}>
                    10-digit Indian mobile. Aapke number pe call aayegi jo OTP boleegi.
                  </p>
                </div>
                <button onClick={handleSendPhoneOTP} disabled={authLoading}
                  style={{
                    width:'100%', padding:'12px', background:OZ_BLUE,
                    color:'#fff', border:'none', borderRadius:theme.radius,
                    cursor: authLoading ? 'not-allowed' : 'pointer', fontWeight:600, fontSize:'0.9rem',
                    opacity: authLoading ? 0.7 : 1,
                  }}>
                  {authLoading ? 'Calling…' : '📞 Call me with OTP'}
                </button>
              </>
            )}

            {phoneStep === 'enter-otp' && (
              <>
                <div style={{marginBottom:14}}>
                  <label style={labelStyle}>Enter OTP heard on call to +91-{phoneInput}</label>
                  <input value={phoneOtpInput}
                    onChange={e => setPhoneOtpInput(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    style={{...inputStyle, fontSize:'1.2rem', letterSpacing:'0.4em', textAlign:'center', fontFamily:'monospace'}}
                    placeholder="0000" maxLength={6}
                    onKeyDown={e => { if (e.key === 'Enter') handleVerifyPhoneOTP(); }}/>
                </div>
                <button onClick={handleVerifyPhoneOTP} disabled={authLoading}
                  style={{
                    width:'100%', padding:'12px', background:OZ_BLUE,
                    color:'#fff', border:'none', borderRadius:theme.radius,
                    cursor: authLoading ? 'not-allowed' : 'pointer', fontWeight:600, fontSize:'0.9rem',
                    opacity: authLoading ? 0.7 : 1,
                  }}>
                  {authLoading ? 'Verifying…' : 'Verify & Sign In'}
                </button>
                <button onClick={() => { setPhoneStep('enter-phone'); setPhoneOtpInput(''); }}
                  style={{
                    width:'100%', padding:'10px', background:'transparent',
                    color:theme.textMuted, border:'none', marginTop:8,
                    cursor:'pointer', fontSize:'0.82rem',
                  }}>
                  ← Change number
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
